import React, { useMemo, useState } from "react";
import { toast } from "react-toastify"; 
import {
    useChatContext,
    useMessageContext,
} from "stream-chat-react";
import {
    MessageActions,
    DefaultDropdownActionButton,
    defaultMessageActionSet,
} from "stream-chat-react/experimental";
import {  APPOINTMENT_STATUS } from "../constants";
import { isAppointmentActionMessage } from "../utils/chatUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCopy,
    faPenToSquare,
    faReply,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useWindowResize } from "../hooks/useWindowResize";

const CopyActionButton = (props) => { 
    const { message } = useMessageContext();
    return (
        <DefaultDropdownActionButton
            {...props}
            onClick={() => {
                if (message?.text) {
                    navigator.clipboard.writeText(message.text);
                }
                if (props.onClick) props.onClick(); // preserve any default behavior
            }}
        >
            <FontAwesomeIcon icon={faCopy} style={{ marginRight: 8 }} />
            Copy Message
        </DefaultDropdownActionButton>
    );
};
const EditActionButton = (props) => {
    const { message } = useMessageContext();
    const { onShowEditModal, ...rest } = props; 

    const handleClick = () => {
        if (onShowEditModal) {
            onShowEditModal(message);
        }
    };

    return (
        <DefaultDropdownActionButton
            {...rest}
            onClick={handleClick}
        >
            <FontAwesomeIcon icon={faPenToSquare} style={{ marginRight: 8 }} />
            Edit Message
        </DefaultDropdownActionButton>
    );
};
 
const DeleteActionButton = (props) => { 
    const { message } = useMessageContext();
    const { client } = useChatContext();
   
    const { onShowDeleteModal, ...rest } = props; 
    const handleClick = () => {
        if (message?.user?.id === client?.userID) {
            if (onShowDeleteModal) {
                onShowDeleteModal(message);
            }
        } else {
            toast.error("You cannot delete others' messages!");
        }
    };

    return (
        <DefaultDropdownActionButton
            {...rest}
            style={{ color: "#e53935" }}
            onClick={handleClick}
        >
            <FontAwesomeIcon icon={faTrash} style={{ marginRight: 8 }} />
            Delete Message
        </DefaultDropdownActionButton>
    );
};

const ReplyActionButton = (props) => {
    
    const { handleOpenThread } = useMessageContext();
    return (
        <DefaultDropdownActionButton
            {...props}
            onClick={() => {
                handleOpenThread && handleOpenThread();
                if (props.onClick) props.onClick();
            }}
        >
            <FontAwesomeIcon icon={faReply} style={{ marginRight: 8 }} />
            Reply
        </DefaultDropdownActionButton>
    );
};
const getOrientation = () =>
  window.matchMedia("(orientation: portrait)").matches ? "portrait" : "landscape";

const ChatMessageActions = (props) => {
    const { message, threadList } = useMessageContext();
    const { client } = useChatContext();
    const { onShowDeleteModal } = props;
    const isSender = message.user?.id === client?.userID;

    // Track orientation and action group open state
    const [orientation, setOrientation] = useState(getOrientation());

    // Close actions on orientation change
    useWindowResize(async () => {
        setOrientation(getOrientation());
    });
    
    const customMessageActionSet = useMemo(() => {
        // If the message has a customType of "appointment", return an empty array
        if (message?.appointment?.type === APPOINTMENT_STATUS.APPOINTMENT || isAppointmentActionMessage(message)) {
            return [];
        }
        // Remove default 'edit' and 'reply' to replace with custom ones
        const filteredDefault = defaultMessageActionSet.filter(
          (action) => action.type !== "edit"
            && action.type !== "reply"
            && action.type !== "flag"
            && action.type !== "pin"
            && action.type !== "mute"
            && action.type !== "quote"
            && action.type !== "markUnread"
            && action.type !== "delete"
        );
        const commonActions = [
            ...filteredDefault,
            {
                type: "copy-message",
                placement: "dropdown",
                Component: CopyActionButton,
            },
            // Only show Reply if not in a Thread
            ...(!threadList
              ? [{
                  type: "reply",
                  placement: "dropdown",
                  Component: ReplyActionButton,
              }]
              : []),
        ];

        if (isSender) {
            return [
                ...commonActions,
                {
                    type: "edit",
                    placement: "dropdown",
                    Component: (actionProps) => (
                      <EditActionButton {...actionProps} onShowEditModal={props.onShowEditModal} />
                    ),
                },
                {
                    type: "delete",
                    placement: "dropdown",
                    Component: (actionProps) => (
                      <DeleteActionButton {...actionProps} onShowDeleteModal={onShowDeleteModal} />
                    ),
                },
            ];
        }
        if(!isSender){
            return[
                ...commonActions,
            ]
        }
        return commonActions;
    }, [message, client, onShowDeleteModal, threadList, orientation, props.onShowEditModal, isSender]);

    return (
      <div>
          <MessageActions
            key={orientation} // Remounts on orientation change, closing the dropdown
            disableBaseMessageActionSetFilter
            messageActionSet={customMessageActionSet}
          />
      </div>
    );
};

export default ChatMessageActions;
